import Vue from "vue";
import Vuex from "vuex";
import { fetchHil } from "./utils";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: {
      isLogged: false,
      id: null,
      role: null,
      finalized: false
    },
    currentProject: null,
    currentProjectProposals: [],
    currentProjectProposalsLoaded: false,
    currentProjectComments: [],
    currentProjectCommentsLoaded: false,
    currentProjectDocuments: [],
    currentProjectDocumentsLoaded: false,
    notifications: [],
    selectables: {}
  },
  mutations: {
    setUserStatus(state, object) {
      state.user = object;
    },

    setCurrentProject(state, project) {
      state.currentProject = project;
      state.currentProjectProposals = [];
      state.currentProjectProposalsLoaded = false;
      state.currentProjectComments = [];
      state.currentProjectCommentsLoaded = false;
    },

    setNotifications(state, notifications) {
      state.notifications = notifications;
    },

    setProposalsForCurrentProject(state, proposals) {
      state.currentProjectProposals = proposals;
      state.currentProjectProposalsLoaded = true;
    },

    setCommentsForCurrentProject(state, comments) {
      state.currentProjectComments = comments;
      state.currentProjectCommentsLoaded = true;
    },

    setDocumentsForCurrentProject(state, comments) {
      state.currentProjectDocuments = comments;
      state.currentProjectDocumentsLoaded = true;
    },

    setNotifsAsSeen(state, updatedNotifications) {
      state.notifications = state.notifications.map(n => {
        if (updatedNotifications.filter(nn => n._id === nn._id).length > 0) {
          return { ...n, seen: true };
        } else {
          return n;
        }
      });
      return state;
    },

    setProjectFeedback(state, ratings) {
      state.currentProject = {
        ...state.currentProject,
        feedback: {
          date: Date.now(), // Fake it until next time it's fetched
          ...ratings
        }
      };
    },

    setProjectVisitAssist(state, answer) {
      state.currentProject = {
        ...state.currentProject,
        wantsVisitAssist: answer
      };
    }
  },
  actions: {
    async login({ dispatch }, { login, password }) {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");

      const refererId = localStorage.getItem("companyReferer");

      const resp = await fetchHil(`/login`, {
        method: "POST",
        headers,
        credentials: "include",
        body: {
          email: login,
          password,
          ...(refererId ? { refererId } : null)
        }
      });

      if (!resp.ok) {
        throw new Error(resp.status);
      }

      const body = await resp.json();
      localStorage.setItem("hil_token", body.token);
      if (window.ReactNativeWebView != null) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            site: "CLIENT",
            token: body.token,
            type: "LOGIN"
          })
        );
      }
      if (body.statusCode < 200 || body.statusCode >= 300) {
        throw new Error(body.statusCode);
      }
      dispatch("fetchStatus");
    },

    async logout({ dispatch }) {
      localStorage.removeItem("hil_token");
      if (window.ReactNativeWebView != null) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "LOGOUT"
          })
        );
      }
      dispatch("fetchStatus");
    },

    async fetchStatus({ commit }) {
      const resp = await (await fetchHil(`/status.json`)).json();
      await commit("setUserStatus", resp);
    },

    async fetchNotifications({ commit }, { role, id }) {
      const resp1 = await (await fetchHil(`/notifications/for/${id}`)).json();
      commit("setNotifications", [...resp1]);
    },

    async fetchProject({ commit, dispatch }, { id }) {
      const response = await fetchHil(`/lease/${id}`);
      if (!response.ok) {
        return;
      }
      const payload = await response.json();
      await commit("setCurrentProject", payload);
      dispatch("fetchProposals", { id });
      dispatch("fetchProjectComments", { id });
    },

    async patchProject({ commit, dispatch }, { id, data }) {
      const resp = await (await fetchHil(`/lease/${id}`, {
        method: "PUT",
        body: data
      })).json();
      await commit("setCurrentProject", resp);
      dispatch("fetchProposals", { id });
      dispatch("fetchProjectComments", { id });
    },

    async fetchProjectForPublicSignature({ commit, dispatch }, { id }) {
      const resp = await (await fetchHil(
        `/lease/signature/public-details/${id}`
      )).json();
      await commit("setCurrentProject", resp);
    },

    async sendFeedback({ commit }, { id, ratings }) {
      await fetchHil(`/lease/${id}/feedback`, {
        method: "PUT",
        body: ratings
      });
      commit("setProjectFeedback", ratings);
    },

    async sendVisitAssist({ commit }, { id, answer }) {
      await fetchHil(`/lease/${id}/assist`, {
        method: "PUT",
        body: { answer }
      });
      commit("setProjectVisitAssist", answer);
    },

    async fetchProposals({ commit }, { id }) {
      const resp = await (await fetchHil(`/proposals/lease/${id}`)).json();
      commit("setProposalsForCurrentProject", resp);
    },

    async fetchProjectComments({ commit }, { id }) {
      const resp = await (await fetchHil(
        `/comments/for/${id}?type=public`
      )).json();
      commit("setCommentsForCurrentProject", resp);
    },

    async fetchDocumentsForProject({ commit }, { id }) {
      const resp = await (await fetchHil(`/documents/for/${id}`)).json();
      commit("setDocumentsForCurrentProject", resp);
    },

    async markNotifsAsRead({ commit, state }, { projectId, estateId = null }) {
      const notifs = state.notifications.filter(
        notif => notif.idProject === projectId && estateId == notif.idEstate
      );
      for (const notif of notifs) {
        await fetchHil(`/notifications/${notif._id}/disable`);
      }
      if (notifs.length > 0) {
        commit("setNotifsAsSeen", notifs);
      }
    }
  }
});
